import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  BookOpen,
  User,
  Plus,
  Download,
  Star,
  TrendingUp,
  Library,
  Target,
  BarChart3,
  Award,
  CheckCircle,
  Zap,
  Brain,
  Activity,
  Rocket,
  Crown,
  Shield,
  Flame,
  Cpu,
  LineChart,
  Calculator,
  Globe,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCourses, getMyEnrollments, enrollCourse } from '../../services/api';

const AdvancedEbooksLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (user) {
      fetchAdvancedEbooks();
      fetchEnrollments();
    }
  }, [user]);

  const fetchAdvancedEbooks = async () => {
    try {
      setLoading(true);
      const response = await getCourses({
        courseType: 'ebook',
        ebookSubcategory: 'advanced',
        limit: 50
      });

      let sortedCourses = response.data.courses || [];

      // Sort courses
      switch (sortBy) {
        case 'newest':
          sortedCourses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'popular':
          sortedCourses.sort((a, b) => (b.enrollmentCount || 0) - (a.enrollmentCount || 0));
          break;
        case 'title':
          sortedCourses.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'chapters':
          sortedCourses.sort((a, b) => (b.totalLessons || 0) - (a.totalLessons || 0));
          break;
        case 'complexity':
          sortedCourses.sort((a, b) => (b.difficultyScore || 0) - (a.difficultyScore || 0));
          break;
        default:
          break;
      }

      setCourses(sortedCourses);
    } catch (error) {
      console.error('Error fetching advanced ebooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await getMyEnrollments({ courseType: 'ebook' });
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await enrollCourse(courseId);
      fetchEnrollments();
    } catch (error) {
      console.error('Error enrolling in ebook:', error);
      alert('Failed to enroll in ebook');
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  const getEnrollment = (courseId) => {
    return enrollments.find((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (user) {
        fetchAdvancedEbooks();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, sortBy]);

  const EbookCard = ({ course }) => {
    const enrolled = isEnrolled(course._id);
    const enrollment = getEnrollment(course._id);

    return (
      <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-200 hover:shadow-xl group relative">
        <div className="aspect-[3/4] overflow-hidden relative">
          <img
            src={course.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=400&h=600&q=80'}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/95 rounded-full flex items-center justify-center">
              <Crown size={24} className="text-red-600" />
            </div>
          </div>
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-2 py-1 rounded bg-red-600 text-white text-xs font-medium">
              <Crown size={12} className="mr-1" />
              Advanced
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-2 py-1 rounded bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-medium">
              <Flame size={12} className="mr-1" />
              Expert
            </span>
          </div>
          {course.averageRating && (
            <div className="absolute bottom-3 right-3">
              <span className="inline-flex items-center px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
                <Star size={12} className="mr-1 text-yellow-400" />
                {course.averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <Rocket size={12} className="mr-1" />
              Expert Level
            </span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              {course.totalLessons || 0} chapters
            </span>
          </div>

          <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4 space-x-4">
            <div className="flex items-center">
              <User size={12} className="mr-1" />
              {course.instructor?.firstName || 'Instructor'}
            </div>
            <div className="flex items-center">
              <Shield size={12} className="mr-1" />
              Premium
            </div>
            <div className="flex items-center">
              <Download size={12} className="mr-1" />
              PDF
            </div>
          </div>

          {enrolled ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Mastery Progress
                </span>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {enrollment?.progressPercentage || 0}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-300"
                  style={{ width: `${enrollment?.progressPercentage || 0}%` }}
                />
              </div>
              <button
                onClick={() => navigate(`/courses/${course._id}`)}
                className="w-full py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium text-sm rounded-lg transition-all duration-200 flex items-center justify-center"
              >
                <Crown size={16} className="mr-2" />
                Continue Mastery
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleEnroll(course._id)}
              className="w-full py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-medium text-sm rounded-lg transition-all duration-200 flex items-center justify-center group"
            >
              <Plus size={16} className="mr-2 group-hover:scale-110 transition-transform" />
              Master This
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#000000]">
      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors flex items-center">
              <Home size={14} className="mr-1" />
              Dashboard
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Courses
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/courses?courseType=ebook" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Ebooks
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-red-600 dark:text-red-400 font-medium">Advanced</span>
          </nav>

          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Crown size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-1">
                Advanced Trading Ebooks
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Master elite trading strategies, quantitative analysis, and institutional-level techniques for professional traders.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                <Crown size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Expert Ebooks
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                <Rocket size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {enrollments.filter((e) => courses.some((c) => c._id === (e.course?._id || e.course))).length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Mastering
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                <Brain size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.reduce((sum, c) => sum + (c.totalLessons || 0), 0)}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Expert Chapters
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                <Star size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length > 0
                    ? (courses.reduce((sum, c) => sum + (c.averageRating || 0), 0) / courses.length).toFixed(1)
                    : '5.0'
                  }
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Elite Rating
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search advanced ebooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            >
              <option value="newest">Latest Release</option>
              <option value="popular">Most Elite</option>
              <option value="title">Alphabetical</option>
              <option value="chapters">Most Comprehensive</option>
              <option value="complexity">Highest Complexity</option>
            </select>
          </div>
        </div>

        {/* Elite Topics */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            Elite Trading Mastery
          </h2>
          <div className="flex flex-wrap gap-3">
            {['Quantitative Finance', 'Institutional Strategies', 'High-Frequency Trading', 'Derivatives Modeling', 'Risk Analytics', 'Market Microstructure', 'Algorithmic Execution', 'Portfolio Optimization', 'Behavioral Finance', 'Machine Learning', 'Statistical Arbitrage', 'Volatility Trading'].map((topic) => (
              <span
                key={topic}
                className="px-4 py-2 bg-[#ffffff] dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:border-red-500 hover:text-red-600 dark:hover:text-red-400 cursor-pointer transition-all"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* Learning Path */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 border border-red-200 dark:border-red-900/30 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <Crown size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-red-800 dark:text-red-400 mb-2">
                Advanced Trading Mastery Path
              </h3>
              <p className="text-sm text-red-700 dark:text-red-500 mb-4">
                Achieve elite-level expertise with institutional-grade strategies, quantitative methods, and cutting-edge trading technologies.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {[
                  { step: 1, title: 'Quantitative Mastery', desc: 'Mathematical modeling', icon: Calculator },
                  { step: 2, title: 'Institutional Methods', desc: 'Professional strategies', icon: Shield },
                  { step: 3, title: 'Technology Integration', desc: 'Algorithmic systems', icon: Cpu },
                  { step: 4, title: 'Global Markets', desc: 'International expertise', icon: Globe }
                ].map((item) => (
                  <div key={item.step} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-3">
                    <div className="flex items-center mb-2">
                      <span className="w-6 h-6 bg-gradient-to-r from-red-600 to-orange-600 text-white text-xs font-bold rounded-full flex items-center justify-center mr-2">
                        {item.step}
                      </span>
                      <item.icon size={16} className="text-red-600 dark:text-red-400 mr-1" />
                      <span className="font-semibold text-sm text-red-800 dark:text-red-300">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400 ml-8">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Advanced */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            Why Choose Advanced Ebooks?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Crown, title: 'Elite Strategies', desc: 'Institutional-grade techniques used by professional traders and hedge funds.', color: 'bg-gradient-to-r from-red-600 to-orange-600' },
              { icon: Cpu, title: 'Quantitative Methods', desc: 'Mathematical models, statistical analysis, and algorithmic trading systems.', color: 'bg-gradient-to-r from-purple-600 to-pink-600' },
              { icon: Rocket, title: 'Market Leadership', desc: 'Cutting-edge insights that position you ahead of market movements.', color: 'bg-gradient-to-r from-indigo-600 to-blue-600' }
            ].map((benefit, index) => (
              <div key={index} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <div className={`w-12 h-12 ${benefit.color} rounded-lg flex items-center justify-center mb-4`}>
                  <benefit.icon size={24} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Prerequisites & Mastery */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10 border border-purple-200 dark:border-purple-900/30 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <Shield size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-purple-800 dark:text-purple-400 mb-2">
                Prerequisites & Elite Skills Development
              </h3>
              <p className="text-sm text-purple-700 dark:text-purple-500 mb-4">
                Master the most sophisticated trading concepts that require extensive experience and mathematical proficiency.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-purple-800 dark:text-purple-300 mb-2">Prerequisites Required:</h4>
                  {[
                    'Advanced technical analysis mastery',
                    'Quantitative finance background',
                    'Programming/algorithmic experience',
                    'Institutional trading knowledge',
                    'Risk management expertise',
                    'Mathematical modeling skills'
                  ].map((item, index) => (
                    <div key={index} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-2 mb-2">
                      <div className="flex items-center">
                        <CheckCircle size={14} className="text-purple-500 mr-2" />
                        <span className="text-xs text-purple-700 dark:text-purple-300">
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-purple-800 dark:text-purple-300 mb-2">Elite Skills You'll Master:</h4>
                  {[
                    'Quantitative strategy development',
                    'High-frequency trading systems',
                    'Advanced derivatives modeling',
                    'Market microstructure analysis',
                    'Institutional execution methods',
                    'Global macro strategies'
                  ].map((item, index) => (
                    <div key={index} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-2 mb-2">
                      <div className="flex items-center">
                        <Crown size={14} className="text-orange-500 mr-2" />
                        <span className="text-xs text-purple-700 dark:text-purple-300">
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What You'll Master */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            What You'll Master at Elite Level
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Calculator,
                title: 'Quantitative Finance',
                topics: ['Stochastic Calculus', 'Monte Carlo Methods', 'Black-Scholes Models', 'Risk Neutral Valuation']
              },
              {
                icon: Cpu,
                title: 'Algorithmic Trading',
                topics: ['Machine Learning', 'Neural Networks', 'Genetic Algorithms', 'High-Frequency Systems']
              },
              {
                icon: LineChart,
                title: 'Market Microstructure',
                topics: ['Order Flow Analysis', 'Liquidity Modeling', 'Market Impact', 'Execution Algorithms']
              },
              {
                icon: Globe,
                title: 'Global Strategies',
                topics: ['Cross-Asset Arbitrage', 'Currency Carry Trades', 'Macro Positioning', 'Emerging Markets']
              }
            ].map((category, index) => (
              <div key={index} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg flex items-center justify-center mr-3">
                    <category.icon size={16} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                    {category.title}
                  </h3>
                </div>
                <div className="space-y-1">
                  {category.topics.map((topic, topicIndex) => (
                    <div key={topicIndex} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {topic}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Elite Certification */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/10 dark:to-orange-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <Award size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-yellow-800 dark:text-yellow-400 mb-2">
                Elite Trading Certification
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-500 mb-4">
                Complete advanced ebooks to earn prestigious certifications recognized by leading financial institutions.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { title: 'Quantitative Analyst', desc: 'Mathematical modeling expertise', icon: Calculator },
                  { title: 'Algorithmic Trader', desc: 'Systematic trading mastery', icon: Cpu },
                  { title: 'Risk Manager', desc: 'Advanced risk analytics', icon: Shield }
                ].map((cert, index) => (
                  <div key={index} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-3">
                    <div className="flex items-center mb-1">
                      <cert.icon size={16} className="text-yellow-600 mr-2" />
                      <span className="font-semibold text-sm text-yellow-800 dark:text-yellow-300">
                        {cert.title}
                      </span>
                    </div>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      {cert.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ebooks Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="aspect-[3/4] bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course) => (
              <EbookCard key={course._id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800">
            <Crown size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
              No advanced ebooks found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search criteria or check back later for new elite-level ebooks.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdvancedEbooksLayout;
